import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useThemeOptional } from '@/context/ThemeContext';
import {
  COLOR_THEMES,
  DEFAULT_COLOR_THEME,
  type ColorTheme,
} from '../../../shared/platform-settings';

/**
 * AR-409 · The one avatar for a WorldVia member, everywhere one is shown:
 * the sidebar user menu, the thread message rows, the Account center, the
 * admin team tab.
 *
 * Photo first — `users.avatarUrl`, which the PRO sync fills from the member's
 * PRO "Profile Image" on sign-in. Without a photo (or when the photo fails to
 * load) a circle in the member's colour theme with their initials in white,
 * instead of the Gravatar "identicon" that used to stand in.
 *
 * The colour is the theme's accent (AR-393). Where white on that accent is
 * not legible the theme's dark step is used instead — that is Coral, whose
 * accent gives white text well under the 3:1 WCAG floor for bold text. No
 * saved theme → Clairvoyant.
 */

interface NameParts {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
}

/**
 * First letter of the first name + first letter of the last name
 * (Britta Roppe → BR). One name → one letter. With no first/last on record the
 * display name is split the same way; failing that, the email's first letter.
 */
export function memberInitials(parts: NameParts): string {
  const first = parts.firstName?.trim() ?? '';
  const last = parts.lastName?.trim() ?? '';
  if (first || last) {
    return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
  }
  const words = (parts.displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }
  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }
  const local = (parts.email ?? '').trim().split('@')[0];
  return local ? local[0].toUpperCase() : '';
}

const channel = (hex: string): number => {
  const c = parseInt(hex, 16) / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** Relative luminance of a #rrggbb colour (WCAG 2.x). */
const luminance = (hex: string): number => {
  const h = hex.replace('#', '');
  return (
    0.2126 * channel(h.slice(0, 2)) +
    0.7152 * channel(h.slice(2, 4)) +
    0.0722 * channel(h.slice(4, 6))
  );
};

const contrastWithWhite = (hex: string): number => 1.05 / (luminance(hex) + 0.05);

/** WCAG 1.4.3 floor for large / bold text; the initials are bold. */
const MIN_CONTRAST = 3;

/**
 * The circle colour for a theme: its accent, or its dark step when white
 * text on the accent would not be legible. Unknown / unsaved theme →
 * Clairvoyant (#6d2a7d).
 */
export function memberAvatarColor(theme: ColorTheme | null | undefined): string {
  const entry =
    COLOR_THEMES.find((t) => t.id === theme) ??
    COLOR_THEMES.find((t) => t.id === DEFAULT_COLOR_THEME)!;
  return contrastWithWhite(entry.accent) >= MIN_CONTRAST ? entry.accent : entry.darkStep;
}

export interface MemberAvatarProps extends NameParts {
  /** The member's photo (`users.avatarUrl`). Null / empty → initials. */
  photoUrl?: string | null;
  /**
   * The theme whose accent colours the initials circle. Omitted → the
   * signed-in viewer's theme (their own avatar). `null` → the default, for a
   * member whose theme is not known to the viewer.
   */
  colorTheme?: ColorTheme | null;
  /** Diameter in px. Omit to size with `className` (h-/w-/text- utilities). */
  size?: number;
  className?: string;
  /** Image alt / accessible name. Defaults to the member's name. */
  alt?: string;
}

export const MemberAvatar: React.FC<MemberAvatarProps> = ({
  photoUrl,
  firstName,
  lastName,
  displayName,
  email,
  colorTheme,
  size,
  className,
  alt,
}) => {
  const theme = useThemeOptional();
  const themeId = colorTheme === undefined ? theme?.colorTheme ?? DEFAULT_COLOR_THEME : colorTheme;

  // A photo that 404s falls back to the initials rather than a broken image.
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [photoUrl]);

  const initials = memberInitials({ firstName, lastName, displayName, email }) || 'U';
  const fullName =
    [firstName, lastName].filter(Boolean).join(' ').trim() || displayName || email || 'Member';
  const label = alt ?? fullName;
  const showPhoto = !!photoUrl && !broken;

  const style: React.CSSProperties | undefined = size
    ? { width: size, height: size, fontSize: Math.round(size * 0.4) }
    : undefined;

  return (
    <span
      data-member-avatar={showPhoto ? 'photo' : 'initials'}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-semibold leading-none text-white',
        // A dark circle on the dark chrome needs an edge to read as a circle.
        !showPhoto && 'dark:ring-1 dark:ring-white/20',
        className,
      )}
      style={{ ...style, backgroundColor: showPhoto ? undefined : memberAvatarColor(themeId) }}
      role={showPhoto ? undefined : 'img'}
      aria-label={showPhoto ? undefined : label}
    >
      {showPhoto ? (
        <img
          src={photoUrl as string}
          alt={label}
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
    </span>
  );
};

export default MemberAvatar;
